import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { useCallback } from 'react';
import { FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import { Row } from 'UIPackage/FormModule/components/Row';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmDataCreated,
  getKnmFormRef,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsNew,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmErknmFormTakenMeasures } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormTakenMeasures';
import {
  PM_TAKEN_MEASURES_BLOCK_ID,
  getTakenMeasuresValueBlockId,
} from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { getKnmPmTypeFormValueIsAnnouncementOfWarning } from 'KnmPmPackage/MainModule/selectors/knmPmSelectors';

import styles from './PmTakenMeasures.module.scss';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';

interface IPmTakenMeasuresProps {
  watchOnly: boolean;
}

interface IPmTakenMeasuresStoreProps {
  formRef: IKnmFormRef | undefined;
  settings: IAdminSetting[];
  created: string | null | undefined;
  isAnnouncementOfWarning: boolean | undefined;
  IsErknmStatusFillInProcess: boolean | undefined;
  isNewKnm: boolean;
}

const PmTakenMeasuresWithoutStore = ({
  watchOnly,
  formRef,
  created,
  settings,
  isNewKnm,
  isAnnouncementOfWarning,
  IsErknmStatusFillInProcess,
}: IPmTakenMeasuresStoreProps & IPmTakenMeasuresProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const takenMeasuresFieldName = 'takenMeasures' as keyof IKnmForm;

  const addField = useCallback(() => {
    const addingField: IKnmErknmFormTakenMeasures = {
      value: '',
      guid: uuidv4(),
    };

    if (!watchOnly && push) push(takenMeasuresFieldName, addingField);
  }, [push, watchOnly]);

  const removeField = useCallback(
    (index: number) => {
      if (!watchOnly && remove) remove(takenMeasuresFieldName, index);
    },
    [remove, watchOnly],
  );

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const isAddFieldEnabled = !watchOnly && isAnnouncementOfWarning && (isNewKnm || IsErknmStatusFillInProcess);

  if (!isEnablePaperlessDocument) return null;

  return (
    <FieldBlock
      label={getTitleFieldWithRedStar({
        label: 'Предложенные меры',
        field: takenMeasuresFieldName,
        isPm: true,
      })}
      id={PM_TAKEN_MEASURES_BLOCK_ID}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          onClick={addField}
          disabled={!isAddFieldEnabled}
        />
      }
    >
      <FieldArray name={takenMeasuresFieldName}>
        {({ fields }) => {
          if (!fields.length) return null;

          return (
            <Row>
              {fields.map((fieldName, sIndex) => {
                const fieldValue: IKnmErknmFormTakenMeasures = fields.value?.[sIndex];
                const id = getTakenMeasuresValueBlockId(fieldValue.guid || '');

                // @ts-ignore
                const takenMeasuresErrors = formRef?.current?.getFieldState(takenMeasuresFieldName)?.error;

                return (
                  <Row key={id} id={id} margin="medium" notWrap isListItem>
                    <TextareaField
                      placeholder="Введите предложенную меру"
                      className={styles.MeasuresTextField}
                      name={`${fieldName}.value`}
                      disabled={watchOnly}
                      validateField={`${takenMeasuresFieldName}`}
                      error={takenMeasuresErrors?.[sIndex]?.value}
                    />
                    {!watchOnly && fields.length && fields.length > 1 && (
                      <CloseButton className="shared-deleting-row-btn" onClick={() => removeField(sIndex)} />
                    )}
                  </Row>
                );
              })}
              {!!fields.length && (
                <div className={styles.HelpText}>
                  <Hint text="Каждую меру необходимо вводить отдельно" />
                </div>
              )}
            </Row>
          );
        }}
      </FieldArray>
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const fieldError = props.errors?.takenMeasures;
          const isFieldTouched = props.touched?.takenMeasures;

          if (isFieldTouched && fieldError && typeof fieldError === 'string') {
            return <p className="shared-input-error">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IPmTakenMeasuresStoreProps => ({
  formRef: getKnmFormRef(state),
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
  isNewKnm: getKnmIsNew(state) || false,
  isAnnouncementOfWarning: getKnmPmTypeFormValueIsAnnouncementOfWarning(state),
  IsErknmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
});

export const PmTakenMeasures = connect(mapStateToProps)(PmTakenMeasuresWithoutStore);
